import { useLocationTestId } from '@/hooks/useTestId'
import { cn } from '@/lib/utils'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { Button as ButtonUI } from '@/global/components/ui/button'
import { Button } from 'global/components/Button'
import AjudaFiltros from 'global/guides/ContentGuide/AjudaFiltros'
import localeText from 'locales/datagrid'
import { BookmarkIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react'
import React from 'react'
import { toast } from 'sonner'
import { filtroVazio } from '../hooks/useDefaultFilterModel'
import FilterModelPro, {
    GridFilterItemPro,
    GridFilterModelPro,
} from '../utils/filterModelPro'
import { useTableApiContext } from '../utils/table-context'
import { GridColDefPro } from '../utils/types'
import FieldColuna from './FieldColuna'
import FieldLinkOperator from './FieldLinkOperator'
import FieldOperator from './FieldOperator'
import FieldValue from './FieldValue'
import FilterPreset from './FilterPreset'

export default function FilterPanelPro() {
    const {
        filterModelPro: filterModel,
        onFilterModelChangePro: onFilterModelChange,
        columns: apiColumns,
    } = useTableApiContext()
    const adicionarFiltroTestId = useLocationTestId('filtros-adicionar')

    const columns = apiColumns.filter(c => c.filterable !== false)

    function gerarLinkFiltro() {
        const link = window.location.href.split('?')[0] + '?grid-filter='
        navigator.clipboard.writeText(
            link + new FilterModelPro(filterModel).toUrlParams(),
        )
        toast.success('Link copiado para a área de transferência')
    }

    return (
        <div
            className={cn(
                'flex flex-row items-start p-2.5 w-full gap-x-2.5',
                'overflow-x-auto h-[150px] overflow-y-hidden',
                'border-1 border-b-0',
            )}
        >
            <div // lista filtros aplicados
                className="flex flex-col items-start flex-1 min-w-[23rem]"
            >
                <div
                    className={cn(
                        'flex flex-col scrollbar-gutter-stable',
                        'my-2 p-2 gap-y-[15px]',
                        'overflow-y-auto max-h-[120px] w-full',
                    )}
                >
                    {!filterModel.items.length ? (
                        <span className="text-base self-center opacity-60 select-none">
                            Nenhum filtro aplicado. Clique em "Adicionar" para
                            começar a filtrar.
                        </span>
                    ) : (
                        filterModel.items.map((item, index) => (
                            <FilterOption
                                key={index}
                                item={item}
                                onFilterModelChange={onFilterModelChange}
                                columns={columns}
                                isFirstItem={index === 0}
                            />
                        ))
                    )}
                </div>
            </div>
            <div>
                <div
                    className={cn(
                        'grid grid-cols-[1fr_0.3fr]',
                        'gap-x-2.5 min-w-fit',
                    )}
                >
                    <div // botoes
                        className="grid grid-rows-[1fr_1fr] gap-y-2.5 min-w-max"
                    >
                        <Button
                            startIcon={<PlusIcon className="size-5" />}
                            variant="outlined"
                            data-testid={adicionarFiltroTestId}
                            onClick={() => {
                                onFilterModelChange(prev => ({
                                    ...prev,
                                    items: [
                                        ...prev.items,
                                        {
                                            columnField: columns[0].field,
                                            operatorValue:
                                                FilterModelPro.getDefaultOperator(
                                                    columns[0].type,
                                                ),
                                            value: '',
                                            id: FilterModelPro.gerarID(
                                                prev.items,
                                            ),
                                        },
                                    ],
                                }))
                            }}
                        >
                            Adicionar
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={
                                <Trash2Icon className="text-error size-5" />
                            }
                            onClick={() =>
                                onFilterModelChange(prev => ({
                                    ...prev,
                                    items: filtroVazio.items,
                                }))
                            }
                            color="error"
                        >
                            Limpar
                        </Button>
                    </div>
                    <div // icones
                        className={cn(
                            'grid grid-rows-[1fr_1fr] gap-x-2.5',
                            'justify-center min-w-fit items-center',
                        )}
                    >
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                            }}
                            title="Clique para ver a ajuda"
                            className="text-primary"
                        >
                            <AjudaFiltros />
                        </div>
                        <ButtonUI
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-10 rounded-full"
                            title="Clique para copiar o link do filtro"
                            onClick={gerarLinkFiltro}
                            disabled={!filterModel.items.length}
                        >
                            <BookmarkIcon />
                        </ButtonUI>
                    </div>
                </div>
                <div // presets
                    className="grid grid-cols-1 min-w-fit mt-2"
                >
                    <FilterPreset
                        onFilterModelChange={onFilterModelChange}
                        filterModel={filterModel}
                        columns={columns}
                    />
                </div>
            </div>
        </div>
    )
}

const FilterOption = ({
    item,
    onFilterModelChange,
    columns,
    isFirstItem,
}: {
    item: GridFilterItemPro
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModelPro>
    >
    columns: GridColDefPro[]
    isFirstItem: boolean
}) => {
    const column = columns.find(column => column.field === item.columnField)

    const MemoizedLinkOperator = React.useMemo(
        () =>
            isFirstItem ? (
                <div className="min-w-[4.5rem] max-w-[4.5rem] flex items-center">
                    <HorizontalDivider className="w-full" />
                </div>
            ) : (
                <FieldLinkOperator
                    item={item}
                    onFilterModelChange={onFilterModelChange}
                    className="min-w-[4.5rem] max-w-[4.5rem]"
                />
            ),
        [item.linkOperator],
    )

    const MemoizedFieldOperator = React.useMemo(
        () =>
            column ? (
                <FieldOperator
                    item={item}
                    column={column}
                    onFilterModelChange={onFilterModelChange}
                    className="min-w-[10rem] max-w-[12rem]"
                />
            ) : null,
        [item.operatorValue, item.columnField],
    )

    const MemoizedFieldColuna = React.useMemo(
        () => (
            <FieldColuna
                item={item}
                columns={columns}
                onFilterModelChange={onFilterModelChange}
                className="min-w-[8rem] max-w-[15rem]"
            />
        ),
        [item.columnField],
    )

    if (!column) return null

    return (
        <div className="flex w-full gap-x-[5px] h-fit">
            <ButtonUI
                type="button"
                variant="ghost"
                size="icon"
                onClick={() =>
                    onFilterModelChange(prev => ({
                        ...prev,
                        items: prev.items.filter(({ id }) => id !== item.id),
                    }))
                }
                title={localeText.filterPanelDeleteIconLabel}
                className="size-10 min-w-10 rounded-full"
            >
                <XIcon className="text-error" />
            </ButtonUI>
            {MemoizedLinkOperator}
            {MemoizedFieldColuna}
            {MemoizedFieldOperator}
            <FieldValue
                item={item}
                column={column}
                onFilterModelChange={onFilterModelChange}
                className="min-w-[8rem] max-w-[15rem]"
            />
        </div>
    )
}
